export type PriorityType = number | { base: number; [system_id: string]: number };

/**
 * Returns the base priority number from either an old number or new priority object.
 */
export const getBasePriority = (priority: any): number => {
  if (typeof priority === "object" && priority !== null) {
    if (typeof priority.base === "number" && !isNaN(priority.base)) {
      return priority.base;
    }
    return 0;
  }
  if (typeof priority === "number" && !isNaN(priority)) {
    return priority;
  }
  return 0;
};

/**
 * Returns the priority for a specific system from either old number or new priority object.
 * Falls back to base priority if the system key does not exist.
 */
export const getSystemPriority = (priority: any, systemId: string): number => {
  if (typeof priority === "object" && priority !== null) {
    if (systemId && typeof priority[systemId] === "number" && !isNaN(priority[systemId])) {
      return priority[systemId];
    }
    if (typeof priority.base === "number" && !isNaN(priority.base)) {
      return priority.base;
    }
  }
  if (typeof priority === "number" && !isNaN(priority)) {
    return priority;
  }
  return 0;
};

/**
 * Returns the priority for a specific complaint from either old number or new priority object.
 * Used for wizard sorting after complaint selection.
 */
export const getComplaintPriority = (priority: any, complaintId?: string): number => {
  if (typeof priority === "object" && priority !== null) {
    if (complaintId && typeof priority[complaintId] === "number" && !isNaN(priority[complaintId])) {
      return priority[complaintId];
    }
    if (typeof priority.base === "number" && !isNaN(priority.base)) {
      return priority.base;
    }
  }
  if (typeof priority === "number" && !isNaN(priority)) {
    return priority;
  }
  return 0;
};

/**
 * Creates the initial priority object when inserting a new item.
 * Sets base = baseVal, and for all selected systems, priority[system_id] = baseVal.
 */
export const createInitialPriority = (
  baseVal: number,
  systemIds?: string[]
): { base: number; [key: string]: number } => {
  const priorityObj: { base: number; [key: string]: number } = { base: baseVal };
  if (Array.isArray(systemIds)) {
    systemIds.forEach((sysId) => {
      if (sysId && sysId !== "null") {
        priorityObj[sysId] = baseVal;
      }
    });
  }
  return priorityObj;
};

/**
 * Ensures that all currently assigned systems exist in the priority object.
 * If not present, they get initialized to the base priority.
 */
export const ensureSystemPriorities = (
  existingPriority: any,
  systemIds?: string[]
): { base: number; [key: string]: number } => {
  let priorityObj: { base: number; [key: string]: number } = { base: 0 };
  if (typeof existingPriority === "object" && existingPriority !== null) {
    priorityObj = { ...existingPriority };
    if (typeof priorityObj.base !== "number") {
      priorityObj.base = 0;
    }
  } else if (typeof existingPriority === "number" && !isNaN(existingPriority)) {
    priorityObj = { base: existingPriority };
  }

  if (Array.isArray(systemIds)) {
    systemIds.forEach((sysId) => {
      if (sysId && sysId !== "null" && typeof priorityObj[sysId] !== "number") {
        priorityObj[sysId] = priorityObj.base;
      }
    });
  }
  return priorityObj;
};
